import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus } from "lucide-react"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { useWhatsappNumbersQuery } from "modules/settings/hooks/whatsapp/useWhatsappNumbersQuery"
import { useTranslation } from "react-i18next"
import {
  Channel,
  ChannelEnum,
  getSupportReplyPolicy,
  SupportCapabilityConfig,
  SupportReplyChannel,
} from "schema"

type Props = {
  value: SupportCapabilityConfig
  onChange: (value: SupportCapabilityConfig) => void
}

type ReplyChannel = (typeof SupportReplyChannel)[keyof typeof SupportReplyChannel]
type OriginChannel = Exclude<Channel, typeof ChannelEnum.Email>

const ORIGIN_CHANNELS = [
  ChannelEnum.Web,
  ChannelEnum.Whatsapp,
  ChannelEnum.Instagram,
  ChannelEnum.Gmail,
  ChannelEnum.Outlook,
  ChannelEnum.Api,
] as const

export function SupportReplyPolicyFields({ value, onChange }: Props) {
  const { t } = useTranslation()
  const { data: integrations, isPending, isError } = useIntegrationsStatusQuery()
  const {
    data: whatsappNumbers,
    isPending: isWhatsappPending,
    isError: isWhatsappError,
  } = useWhatsappNumbersQuery()
  const policies = value.replyPolicyByChannel ?? {}
  const availability = {
    [ChannelEnum.Web]: true,
    [ChannelEnum.Api]: true,
    [ChannelEnum.Whatsapp]: Boolean(
      whatsappNumbers?.some((number) => number.status === "CONNECTED"),
    ),
    [ChannelEnum.Instagram]: Boolean(integrations?.instagram),
    [ChannelEnum.Gmail]: Boolean(integrations?.gmail),
    [ChannelEnum.Outlook]: Boolean(integrations?.outlook),
  }
  const visibleChannels = ORIGIN_CHANNELS.filter((channel) => availability[channel])
  const overriddenChannels = visibleChannels.filter((channel) => policies[channel] !== undefined)
  const availableChannels = visibleChannels.filter((channel) => policies[channel] === undefined)

  function removeOverride(channel: OriginChannel) {
    const replyPolicyByChannel = { ...policies }
    delete replyPolicyByChannel[channel]

    onChange({ ...value, replyPolicyByChannel })
  }

  function updatePrimary(channel: OriginChannel, primary: ReplyChannel) {
    const alternative = policies[channel]?.alternative

    onChange({
      ...value,
      replyPolicyByChannel: {
        ...policies,
        [channel]: {
          primary,
          ...(alternative && alternative !== primary ? { alternative } : {}),
        },
      },
    })
  }

  function updateAlternative(channel: OriginChannel, alternative: ReplyChannel | "none") {
    const policy = getSupportReplyPolicy(value, channel)

    onChange({
      ...value,
      replyPolicyByChannel: {
        ...policies,
        [channel]: {
          primary: policy.primary,
          ...(alternative !== "none" ? { alternative } : {}),
        },
      },
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">
          {t("capabilities.catalog.Support.config.replyPolicy.label")}
        </span>
        <p className="text-sm text-muted-foreground">
          {t("capabilities.catalog.Support.config.replyPolicy.description")}
        </p>
      </div>
      {(isPending || isWhatsappPending) && (
        <p className="text-sm text-muted-foreground">
          {t("capabilities.catalog.Support.config.replyPolicy.loadingChannels")}
        </p>
      )}
      {(isError || isWhatsappError) && (
        <p role="alert" className="text-sm text-destructive">
          {t("capabilities.catalog.Support.config.replyPolicy.channelsError")}
        </p>
      )}
      {overriddenChannels.map((channel) => {
        const policy = getSupportReplyPolicy(value, channel)

        return (
          <div key={channel} className="flex flex-col gap-2 rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {t(`capabilities.catalog.Support.config.replyPolicy.origins.${channel}`)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeOverride(channel)}
              >
                {t("capabilities.catalog.Support.config.replyPolicy.removeOverride")}
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`support-reply-primary-${channel}`}>
                  {t("capabilities.catalog.Support.config.replyPolicy.primary")}
                </Label>
                <Select
                  value={policy.primary}
                  onValueChange={(primary: ReplyChannel) => updatePrimary(channel, primary)}
                >
                  <SelectTrigger id={`support-reply-primary-${channel}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(SupportReplyChannel).map((replyChannel) => (
                      <SelectItem key={replyChannel} value={replyChannel}>
                        {t(
                          `capabilities.catalog.Support.config.replyPolicy.channels.${replyChannel}`,
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`support-reply-alternative-${channel}`}>
                  {t("capabilities.catalog.Support.config.replyPolicy.alternative")}
                </Label>
                <Select
                  value={policy.alternative ?? "none"}
                  onValueChange={(alternative: ReplyChannel | "none") =>
                    updateAlternative(channel, alternative)
                  }
                >
                  <SelectTrigger id={`support-reply-alternative-${channel}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t("capabilities.catalog.Support.config.replyPolicy.none")}
                    </SelectItem>
                    {Object.values(SupportReplyChannel)
                      .filter((replyChannel) => replyChannel !== policy.primary)
                      .map((replyChannel) => (
                        <SelectItem key={replyChannel} value={replyChannel}>
                          {t(
                            `capabilities.catalog.Support.config.replyPolicy.channels.${replyChannel}`,
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )
      })}
      {availableChannels.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="self-start">
              <Plus className="w-4 h-4" />
              <span>{t("capabilities.catalog.Support.config.replyPolicy.addOverride")}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {availableChannels.map((channel) => (
              <DropdownMenuItem
                key={channel}
                onSelect={() => updatePrimary(channel, SupportReplyChannel.Email)}
              >
                {t(`capabilities.catalog.Support.config.replyPolicy.origins.${channel}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {visibleChannels.some(
        (channel) => getSupportReplyPolicy(value, channel).primary === SupportReplyChannel.Email,
      ) && (
        <p className="text-sm text-muted-foreground">
          {t("capabilities.catalog.Support.config.replyPolicy.emailRequirement")}
        </p>
      )}
    </div>
  )
}
